import { BadgeCheck, Info, Loader2, RefreshCw, Scale, ShieldCheck, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatBRL } from "../../lib/format";
import { usePixCheckout } from "../../hooks/usePixCheckout";
import type { CheckoutBuyer, ReleasedDocument } from "../../types/api";
import { PixCheckoutModal } from "./PixCheckoutModal";

export interface Seal {
  id: string;
  label: string;
  icon: LucideIcon;
}

const DEFAULT_TITLE = "Liberar Documento Completo em PDF + Roteiro de Envio";

const DEFAULT_SEALS: Seal[] = [
  { id: "emissao", label: "Emissão imediata", icon: Zap },
  { id: "conformidade", label: "Conformidade com a Lei nº 13.786/18", icon: Scale },
  { id: "garantia", label: "Verificação de prazo", icon: ShieldCheck },
];

interface Props {
  price: number;
  protocolo: string;
  buyer: CheckoutBuyer;
  onReleased: (doc: ReleasedDocument) => void;
  onRegenerate: () => void;
  title?: string;
  seals?: Seal[];
}

export function ConversionBlock({ price, protocolo, buyer, onReleased, onRegenerate, title = DEFAULT_TITLE, seals = DEFAULT_SEALS }: Props) {
  const pix = usePixCheckout({ protocolo, buyer, price, onReleased });
  const creating = pix.phase === "creating" && !pix.open;
  const inlineError = !pix.open ? pix.error : null;

  return (
    <div data-testid="conversion-block" className="mx-auto mt-8 w-full max-w-[794px] rounded-md border border-emerald-500/40 bg-slate-900 p-6 sm:p-8">
      <h3 className="text-lg font-semibold leading-snug text-slate-100 sm:text-xl">
        {title}
      </h3>
      <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p data-testid="conversion-price" className="text-3xl font-semibold text-slate-100">
            {formatBRL(price)} <span className="text-sm font-normal text-slate-400">(One-Shot)</span>
          </p>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">Pagamento único</p>
        </div>
        <button
          type="button"
          onClick={pix.start}
          disabled={creating}
          data-testid="release-pix-button"
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-500 active:scale-[0.98] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
        >
          {creating ? <Loader2 aria-hidden="true" className="h-4 w-4 motion-safe:animate-spin" /> : <BadgeCheck aria-hidden="true" className="h-4 w-4" />}
          {creating ? "Gerando cobrança PIX..." : "Liberar Notificação Imediata (PIX)"}
        </button>
      </div>
      {pix.session && pix.phase === "pending" && !pix.open && (
        <p data-testid="pix-pending-notice" className="mt-4 text-xs text-slate-400">Cobrança PIX em aberto — clique no botão acima para retomar o pagamento.</p>
      )}
      {inlineError && (
        <div role="alert" data-testid="checkout-error" className="fade-up mt-6 flex flex-col gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2"><Info aria-hidden="true" className="h-4 w-4 shrink-0" />{inlineError.message}</span>
          {inlineError.code === "PREVIEW_EXPIRED" && (
            <button type="button" onClick={onRegenerate} data-testid="regenerate-preview-button" className="btn-secondary min-h-[36px] py-1.5 text-xs">
              <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />Gerar nova prévia
            </button>
          )}
        </div>
      )}
      <ul className="mt-8 grid gap-3 border-t border-slate-800 pt-6 sm:grid-cols-3">
        {seals.map(({ id, label, icon: Icon }) => (
          <li key={id} data-testid={`seal-${id}`} className="flex items-center gap-2 text-xs text-slate-300">
            <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-emerald-500" />{label}
          </li>
        ))}
      </ul>
      <PixCheckoutModal
        open={pix.open}
        onClose={() => pix.setOpen(false)}
        session={pix.session}
        phase={pix.phase}
        remainingMs={pix.remainingMs}
        price={price}
        error={pix.error}
        simulating={pix.simulating}
        onRenew={() => void pix.renew()}
        onSimulate={() => void pix.simulate()}
      />
    </div>
  );
}
